import React, { useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { useNavContext } from './useNavContext'
import DesktopNav from '@toasttab/shared/src/components/organisms/Navigation/Contentful/Desktop/DesktopNav'
import MobileNav from '@toasttab/shared/src/components/organisms/Navigation/Contentful/Mobile/MobileNav'
import Button from '@toasttab/shared/src/components/atoms/Button/Button'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import useWindowSize from '@toasttab/shared/src/hooks/useWindowSize'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import NavRegionSwitcher from './RegionMenu/NavRegionSwitcher'
import SalesCta from './SalesCta'
import UtilityNav from './UtilityNav'

const MainNavigation = () => {
  const { t } = useI18next()
  const {
    isKeyboardNavigation,
    isMobileNavOpen,
    heapClasses,
    navigationData,
    selectedParentMenu,
    setIsKeyboardNavigation,
    setIsMobileNavOpen,
    setIsOverlayOpen,
    setIsRegionMenuOpen,
    setSelectedParentMenu,
    setSelectedTabbedMenu,
    showLogin,
    showRequestDemo,
    showRestaurantSearch,
    useUtilityNav
  } = useNavContext()

  const windowSize = useWindowSize()
  const isDesktopLayout = windowSize.width >= 1024
  const { headerHeight, setHeaderHeight } = usePageContext()
  useEffect(() => {
    // Close desktop menus when window changes to mobile size & vice versa
    const closeMenus = () => {
      if (isDesktopLayout && isMobileNavOpen) {
        setIsMobileNavOpen(false)
        setIsOverlayOpen(false)
        setIsRegionMenuOpen(false)
        setSelectedTabbedMenu(null)
        setSelectedParentMenu(null)
      } else if (!isDesktopLayout && selectedParentMenu !== null) {
        setSelectedParentMenu(null)
        setIsRegionMenuOpen(false)
        setIsOverlayOpen(false)
        setSelectedTabbedMenu(null)
      }
    }
    window.addEventListener('resize', closeMenus)
  }, [
    isDesktopLayout,
    isMobileNavOpen,
    selectedParentMenu,
    setIsMobileNavOpen,
    setIsOverlayOpen,
    setIsRegionMenuOpen,
    setSelectedParentMenu,
    setSelectedTabbedMenu
  ])

  useEffect(() => {
    const handleTab = (event: any) => {
      if (
        (event.key.toLowerCase() === 'tab' || event.keyCode === 9) &&
        !isKeyboardNavigation
      ) {
        setIsKeyboardNavigation(true)
      }
    }
    window.addEventListener('keydown', handleTab)
  }, [isKeyboardNavigation, setIsKeyboardNavigation])

  useEffect(() => {
    const handleEsc = (event: any) => {
      if (
        event.key === 'Escape' ||
        event.key === 'ESC' ||
        event.keyCode === 27
      ) {
        setIsMobileNavOpen(false)
        setIsOverlayOpen(false)
        setIsRegionMenuOpen(false)
        setSelectedParentMenu(null)
      }
    }
    window.addEventListener('keydown', handleEsc)
  }, [
    setIsMobileNavOpen,
    setIsOverlayOpen,
    setIsRegionMenuOpen,
    setSelectedParentMenu
  ])

  const navCss = classNames('relative z-[100]', heapClasses.mainNav, {
    'border-b border-color-gray-25': navigationData?.menus
  })
  const navInnerCss = classNames(
    'flex flex-nowrap z-[100] bg-white items-center justify-between w-full h-[55px] lg:h-[71px] px-4 lg:px-11 font-effra max-w-[1128px] xl:px-0 m-auto'
  )
  const baseHamburgerCss =
    'block absolute h-0.5 w-5 bg-gray-125 transform transition duration-500 ease-in-out'
  const hamburgerTopCss = classNames(baseHamburgerCss, {
    'rotate-45': isMobileNavOpen,
    '-translate-y-1.5': !isMobileNavOpen
  })
  const hamburgerMidCss = classNames(baseHamburgerCss, {
    'opacity-0': isMobileNavOpen
  })
  const hamburgerBottomCss = classNames(baseHamburgerCss, {
    '-rotate-45': isMobileNavOpen,
    'translate-y-1.5': !isMobileNavOpen
  })

  const loginCss = classNames(
    heapClasses.login,
    '!font-semibold lg:block whitespace-nowrap px-[22px] py-[10px]',
    {
      hidden: navigationData?.menus?.length,
      'mr-2': showRequestDemo
    }
  )
  const requestDemoCss = classNames(
    heapClasses.requestDemo,
    '!font-semibold hidden lg:block px-[22px] py-[10px] text-nowrap'
  )

  const toggleMobileMenu = () => {
    setIsMobileNavOpen(!isMobileNavOpen)
    setSelectedParentMenu(null)
    setIsRegionMenuOpen(false)
    // Re-set headerHeight when nav is opened, to ensure it's in the right position
    const headHeight =
      typeof document !== 'undefined'
        ? document.querySelector('.headerContent')?.clientHeight
        : 0
    if (headHeight > 0 && headHeight !== headerHeight) {
      setHeaderHeight(headHeight)
    }
  }
  const loginVariation =
    showRequestDemo || (!showRequestDemo && isDesktopLayout)
      ? 'secondary'
      : !navigationData?.menus?.length
      ? 'text'
      : 'primary'

  return (
    <nav
      aria-label='Toast Main Navigation'
      className={navCss}
      data-testid='MainNavigation'
    >
      {useUtilityNav ? <UtilityNav /> : null}
      <div className={navInnerCss} data-testid='MainNavigation-desktopNav'>
        <DesktopNav menus={navigationData?.menus} />
        <div className='flex flex-nowrap items-center ml-auto'>
          {showRestaurantSearch && !useUtilityNav ? (
            <a
              className={`${heapClasses.restaurantSearch} mr-6 font-effra text-[15px] font-normal text-default -mt-2 before:content-icon-nav-location before:relative before:top-[6px] before:mr-1 hidden 2xl:block`}
              href='https://www.toasttab.com/local?referrer=pos'
            >
              {t('Restaurants')}
            </a>
          ) : null}
          {!useUtilityNav ? (
            <>
              <SalesCta />
              <NavRegionSwitcher />
            </>
          ) : null}
          {showLogin && navigationData?.loginUrl && !useUtilityNav ? (
            <Button
              classes={loginCss}
              dataTestId='MainNavigation-loginButton'
              href={navigationData.loginUrl}
              variation={loginVariation}
            >
              {t('Login', { defaultValue: 'Log In' })}
            </Button>
          ) : null}
          {showRequestDemo && navigationData?.loginUrl ? (
            <Button
              classes={requestDemoCss}
              dataTestId='MainNavigation-demoButton'
              href={navigationData.demoUrl}
            >
              {t('Get a Demo')}
            </Button>
          ) : null}
        </div>
        {navigationData?.menus?.length ? (
          <div className='flex lg:hidden flex-nowrap items-center pb-1 font-effra'>
            <button
              aria-controls='mobile-nav-menu'
              aria-expanded={isMobileNavOpen}
              aria-haspopup
              aria-label='Toggle Toast Mobile Navigation'
              className='w-5 h-5'
              onClick={toggleMobileMenu}
            >
              <span
                aria-hidden
                className={hamburgerTopCss}
                data-testid='MainNavigation-hamburger'
              />
              <span
                aria-hidden
                className={hamburgerMidCss}
                data-testid='MainNavigation-hamburger'
              />
              <span
                aria-hidden
                className={hamburgerBottomCss}
                data-testid='MainNavigation-hamburger'
              />
            </button>
          </div>
        ) : null}
      </div>
      {navigationData?.menus ? <MobileNav /> : null}
    </nav>
  )
}

export default MainNavigation
